<template>
  <div
    class="custom-select"
    :class="{'disabled': disabled}"
    :tabindex="disabled ? -1 : 0"
    @focus="enableOpenDropdown"
    @blur="disableOpenDropdown"
  >
    <div class="relative">
      <div
        class="relative flex"
        @click="toggleOpen"
      >
        <div class="selected-item">
          <slot name="active" :selected="selected"></slot>
        </div>

        <div
          v-if="!disabled"
          class="arrow-indicator text-muted flex flex-align-center"
        >
          <svg v-if="!isOpen" class="block" style="width: 1em; height: 1em;" fill="currentColor" viewBox="0 0 100 100">
            <polygon points="10 25, 90 25, 50 75"/>
          </svg>
          <svg v-else-if="isOpen" class="block" style="width: 1em; height: 1em;" fill="currentColor" viewBox="0 0 100 100">
            <polygon points="10 75, 90 75, 50 25"/>
          </svg>
        </div>
        <svg v-else class="block text-muted" style="margin-left: -24px; height: 1.75em; width: 18px;" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <use href="#input-lock-icon"></use>
        </svg>
      </div>

      <div :class="[isOpen ? 'block' : 'hidden']">

        <button @click="isOpen = false" type="button" class="backdrop"></button>

        <div class="dropdown" ref="scrollContainer" :style="`max-height: ${maxHeight}`">
          <div
            v-for="(item, i) in items"
            :key="`item-${i}`"
            ref="items"
            @click.left="selectItem(i)"
          >
            <slot
              name="item"
              :current="currentItem === i"
              :item="item"
              :index="i"
            />
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    items: {
      default: () => [],
      type: Array,
    },
    preventBackgroundScrolling: {
      default: true,
      type: Boolean,
    },
    selected: {
      type: Object,
    },
  },

  data() {
    return {
      currentItem: 0,
      isOpen: false,
      maxHeight: (60 * 3.5) + 'px',
    }
  },

  created() {
    this.currentItem = Math.max(0, this.items.findIndex((element) => {
      return element === this.selected;
    }));
  },

  mounted() {
    document.addEventListener('keydown', this.onEscape);
  },

  destroyed() {
    document.removeEventListener('keydown', this.nextItem);
    document.removeEventListener('keydown', this.onEscape);
  },

  methods: {
    disableOpenDropdown() {
      document.removeEventListener('keydown', this.keyboardToggleDropdown);

      this.$nextTick(() => setTimeout(() => this.isOpen = false, 500));
    },

    enableOpenDropdown() {
      document.addEventListener('keydown', this.keyboardToggleDropdown);
    },

    keyboardToggleDropdown(event) {
      if(this.disabled) {
        return;
      }

      if(this.isOpen === false) {
        if(event.keyCode === 40) { // arrow down
          this.isOpen = true;

          event.preventDefault();
        }
      } else {
        if(event.keyCode === 9) { // tab
          event.preventDefault();
        }

        if(event.keyCode === 13) { // enter
          this.selectItem(this.currentItem);
        }
      }
    },

    onEscape(event) {
      if (!this.isOpen || event.keyCode !== 27) { // escape
        return;
      }

      this.isOpen = false
    },

    nextItem(event) {
      if(!this.isOpen) {
        return;
      }

      if(event.keyCode === 38 && this.currentItem > 0) { // arrow up
        this.currentItem--;
      } else if(event.keyCode === 40 && this.currentItem < this.items.length - 1) { // arrow down
        this.currentItem++;
      }

      if([38, 40].indexOf(event.keyCode) !== -1) { // arrow up/down
        event.preventDefault();

        this.scrollDropdown();
      }
    },

    scrollDropdown() {
      const itemH        = this.currentItem > -1 ? this.$refs.items[this.currentItem].clientHeight : 0;
      const itemTop      = this.currentItem > -1 ? this.$refs.items[this.currentItem].offsetTop : 0;
      const containerH   = this.$refs.scrollContainer.clientHeight;
      const containerTop = this.$refs.scrollContainer.scrollTop;

      if(itemTop + itemH > containerH + containerTop) {
        this.$refs.scrollContainer.scrollTop = itemTop - containerH + itemH;
      } else if(itemTop < containerTop) {
        this.$refs.scrollContainer.scrollTop = itemTop;
      }
    },

    selectItem(index) {
      this.currentItem = index;

      this.$emit('update:selected', this.items[index]);

      this.isOpen = false;
    },

    toggleOpen() {
      if(this.disabled) {
        return false;
      }

      this.isOpen = !this.isOpen;
    },
  },

  watch: {
    isOpen(newState) {
      this.currentItem = this.items.findIndex((element) => {
        return element === this.selected;
      });

      if(newState) {
        this.scrollDropdown();

        document.addEventListener('keydown', this.nextItem);

        if(!this.preventBackgroundScrolling) return;

        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
      } else {
        document.removeEventListener('keydown', this.nextItem);

        if(!this.preventBackgroundScrolling) return;

        document.documentElement.style.overflow = null;
        document.body.style.overflow = null;
      }
    },
  },
}
</script>

<style scoped>
.backdrop {
  -webkit-appearance: none;
  background: transparent;
  border: 0;
  bottom: 0;
  cursor: default;
  display: block;
  height: 100%;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  width: 100%;
  z-index: 1000;
}

.custom-select {
  background-color: #FFF;
  border: 1px solid #979797;
  border-radius: 4px;
}
.custom-select:hover {
  cursor: pointer;
}
.custom-select:focus {
  border-color: #00549d;
  box-shadow: inset 0 0.2em 0.5em rgba(0,0,0,.09),
              0 0 0.5em 0.1em rgba(0,84,157,.75);
  outline: none;
}
.custom-select.disabled, .custom-select.disabled:hover, .custom-select.disabled:focus {
  border-color: #979797 !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
  background: transparent;
}

.arrow-indicator {
  background: #FFF;
  background: linear-gradient(-90deg, #FFF 50%, rgba(255, 255, 255, 0.3) 90%);
  border-radius: 4px;
  margin-left: -1.5em;
  width: 1em;
}

.dropdown {
  background-color: #FEFEFE;
  border: 1px solid #00549d;
  border-radius: 4px;
  box-shadow: inset 0 0.2em 0.5em rgba(0,0,0,.09),
              0 0 0.5em 0.1em rgba(0,84,157,.75);
  left: 0;
  margin-left: -1px;
  margin-right: -1px;
  margin-top: .5em;
  overflow-y: scroll;
  position: absolute;
  right: 0;
  z-index: 1010;
}

.dropdown > div + div {
  border-top: 1px solid lightgray;
}

.selected-item {
  width: 100%;
}
.disabled .template-item {
  cursor: not-allowed !important;
}
.disabled .selected-item {
  padding-right: 1.75em;
}
</style>
